/**
 * Helpers for items whose `unit` is a pack (tub, drum, box) and that contain
 * a measurable sub-quantity (kg, L, pcs). Pack-size display only — take/receive
 * still operate in whole packs.
 */

const fmtNum = (n, digits = 2) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const f = Number(n);
  let out;
  if (Math.abs(f) >= 100) out = f.toFixed(0);
  else if (Math.abs(f) >= 10) out = f.toFixed(1);
  else out = f.toFixed(digits);
  // Strip trailing zeros so 20.0 → 20 and 6.50 → 6.5 (still keeps
  // significant fractional digits like 6.55).
  return Number(out).toString();
};

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `$${Number(n).toFixed(2)}`;
};

/**
 * Display a quantity without IEEE-754 tails like 6.550000000000001.
 * Integers pass through untouched (avoids "10.00 sheets"); fractions
 * are rounded to 2 dp and any trailing zeros stripped so 6.55 stays
 * "6.55" but 6.50 becomes "6.5" and 6.00 becomes "6".
 */
export const fmtQty = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  if (Number.isInteger(v)) return String(v);
  return Number(v.toFixed(2)).toString();
};

/**
 * Returns the derived totals for an item with pack info, or null if not
 * applicable.
 */
export const packTotals = (item) => {
  const size = Number(item?.pack_size) || 0;
  const unit = (item?.pack_unit || "").trim();
  if (size <= 0 || !unit) return null;
  const qty = Number(item?.current_qty) || 0;
  const cost = Number(item?.unit_cost) || 0;
  return {
    size,
    unit,
    qtyTotal: qty * size,
    costPerSub: cost > 0 ? cost / size : 0,
  };
};

/**
 * Short human-readable summary, e.g.
 *   "25 kg per tub · 1,000 kg total · $8.31/kg ≡ $207.75/tub"
 */
export const packSummary = (item) => {
  const t = packTotals(item);
  if (!t) return null;
  const packUnit = item.unit || "pack";
  const parts = [`${fmtNum(t.size)} ${t.unit} per ${packUnit}`];
  if (t.qtyTotal > 0) {
    parts.push(`${fmtNum(t.qtyTotal)} ${t.unit} total`);
  }
  if (t.costPerSub > 0) {
    parts.push(
      `${fmtMoney(t.costPerSub)}/${t.unit} ≡ ${fmtMoney(
        Number(item.unit_cost) || 0,
      )}/${packUnit}`,
    );
  }
  return parts.join(" · ");
};
